import { useEffect, useRef, useState } from 'react';
import { TRACKS, TRACK_CATEGORIES, TRACK_DIFFICULTIES } from '../constants/appConstants.js';
import { supabase } from '../services/supabaseClient.js';

// AdaptOS design-system tokens for this page.
const AMBER = 'var(--brand-amber)';
const SLATE = 'var(--brand-slate)';
const INK   = '#1e1e2e';

// ─────────────────────────────────────────────
// TRACK SELECTOR — learning-only. Browse tracks → open a detail panel → start.
// No billing, pricing, upgrade CTAs, or career/job-market data live here
// anymore (billing now lives on /account; jobs were removed entirely). The
// detail modal surfaces what a track IS — description, exam logistics, the
// roles it maps to, and track stats — then routes the learner into study.
// ─────────────────────────────────────────────

const C = {
  bg:       'var(--bg-primary)',
  panel:    'var(--bg-secondary)',
  card:     'var(--bg-elevated)',
  border:   'var(--border-soft)',
  muted:    'var(--text-muted)',
  dim:      'var(--text-secondary)',
  teal:     'var(--accent-primary)',
  cream:    'var(--bg-card)',
};

// ─────────────────────────────────────────────
// ROADMAP REMINDER CARD — compact, career-path focused. Replaces the old
// ContinueLearningWidget at the top of the Certifications page. Reminds the
// learner of the certification path they're on and where it leads, then
// links to the Roadmap screen. Not a lesson-resume card.
// ─────────────────────────────────────────────

// Natural certification progressions — the chain rendered in the reminder.
const CERT_PATHS = {
  comptia_aplus:    ['comptia_aplus', 'comptia_network', 'comptia_security'],
  comptia_network:  ['comptia_aplus', 'comptia_network', 'comptia_security'],
  comptia_security: ['comptia_aplus', 'comptia_network', 'comptia_security'],
  az_900:           ['az_900', 'az_104'],
  az_104:           ['az_900', 'az_104'],
};

function shortLabel(label) {
  return (label || '').replace(/^(CompTIA|Microsoft|Google|AWS|Cisco)\s+/i, '');
}

const reminderShell = {
  background: '#ffffff', borderRadius: 12, boxShadow: 'var(--card-shadow)',
  border: '1px solid #e5e7eb', borderLeft: `4px solid ${AMBER}`,
  padding: '14px 18px', marginBottom: 18,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
};
const pathLabel = { fontSize: 10, fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase', color: AMBER, margin: 0 };
const viewRoadmapLink = {
  background: 'transparent', border: 'none', color: AMBER, fontSize: 13, fontWeight: 700,
  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', padding: '6px 2px',
};

function RoadmapReminderCard({ learner, onNavigate, onExplore }) {
  const trackId = learner?.current_track;
  const track   = trackId ? TRACKS[trackId] : null;

  // Empty state — no active track yet.
  if (!track) {
    return (
      <div style={reminderShell}>
        <div style={{ minWidth: 0 }}>
          <p style={pathLabel}>Your Path</p>
          <p style={{ fontSize: 13.5, fontWeight: 600, color: INK, margin: '5px 0 0' }}>
            Start with a certification track below to build your path.
          </p>
        </div>
        <button type="button" onClick={onExplore} style={viewRoadmapLink}>Explore Tracks →</button>
      </div>
    );
  }

  const destination = learner?.career_goal || track.job_roles?.[0] || 'Your career goal';
  const path = CERT_PATHS[trackId] || [trackId];

  return (
    <div style={reminderShell}>
      <div style={{ minWidth: 0 }}>
        <p style={pathLabel}>Your Path</p>
        <p style={{ fontSize: 14, fontWeight: 700, color: INK, margin: '3px 0 7px', lineHeight: 1.25 }}>{destination}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          {path.map((id, i) => {
            const current = id === trackId;
            return (
              <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 12, fontWeight: current ? 800 : 600, color: current ? AMBER : SLATE }}>
                  {shortLabel(TRACKS[id]?.label || id)}
                </span>
                {i < path.length - 1 && <span style={{ color: '#cbd2dc', fontSize: 12, fontWeight: 700 }} aria-hidden>→</span>}
              </span>
            );
          })}
        </div>
      </div>
      <button type="button" onClick={() => onNavigate?.('roadmap')} style={viewRoadmapLink}>View Roadmap →</button>
    </div>
  );
}

// ─────────────────────────────────────────────
// TRACK CATALOG GATING
// ─────────────────────────────────────────────
const COMING_SOON_TRACKS = [
  'ab_900',
  'google_it_support', 'aws_ccp', 'comptia_linux', 'aws_saa', 'comptia_pentest',
  'itil_foundation', 'pmp', 'cisco_ccna', 'cissp', 'aws_sap', 'cisco_ccie', 'rhca', 'gcp_pca',
];
// AZ-900 and AZ-104 are live. AB-900 is in beta testing (curriculum seeded,
// 3 modules / 21 lessons).
const BETA_TESTING_TRACKS = new Set(['ab_900']);

const DIFFICULTY_STYLE = {
  Beginner:     { color: 'var(--brand-emerald)', bg: 'rgba(5,150,105,0.12)',  border: 'rgba(5,150,105,0.40)' },
  Intermediate: { color: 'var(--brand-amber)',   bg: 'rgba(217,119,6,0.12)',  border: 'rgba(217,119,6,0.40)' },
  Advanced:     { color: '#7c3aed',              bg: 'rgba(124,58,237,0.12)', border: 'rgba(124,58,237,0.40)' },
};

function FilterChip({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 13px', borderRadius: 99, fontSize: 11.5, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
      border: `1px solid ${active ? AMBER : '#e5e7eb'}`,
      background: active ? '#fffaf0' : '#ffffff',
      color: active ? AMBER : SLATE, whiteSpace: 'nowrap',
    }}>{label}</button>
  );
}

// ─────────────────────────────────────────────
// TRACK DETAIL MODAL — opened on card click. Shows what the track is, exam
// logistics, mapped job roles, and stats, then routes into study via the CTA.
// ─────────────────────────────────────────────
function TrackDetailModal({ track, learner, onClose, onSelectTrack }) {
  const isComingSoon = COMING_SOON_TRACKS.includes(track.id);
  const isBeta       = BETA_TESTING_TRACKS.has(track.id);
  const diffStyle    = DIFFICULTY_STYLE[track.difficulty];
  const statusLabel  = isComingSoon ? 'Coming Soon' : isBeta ? 'Beta' : 'Active';

  const [stats, setStats] = useState({ loading: true, lessons: null, started: false });

  useEffect(() => {
    let cancelled = false;
    if (isComingSoon) { setStats({ loading: false, lessons: 0, started: false }); return; }
    (async () => {
      try {
        const [lessonsCount, startedCount] = await Promise.all([
          supabase.from('lessons').select('id, modules!inner(track_id)', { count: 'exact', head: true }).eq('modules.track_id', track.id),
          learner?.id
            ? supabase.from('lesson_progress').select('id', { count: 'exact', head: true }).eq('learner_id', learner.id).eq('track_id', track.id)
            : Promise.resolve({ count: 0 }),
        ]);
        if (cancelled) return;
        setStats({ loading: false, lessons: lessonsCount?.count || 0, started: (startedCount?.count || 0) > 0 });
      } catch {
        if (!cancelled) setStats({ loading: false, lessons: null, started: false });
      }
    })();
    return () => { cancelled = true; };
  }, [track.id, learner?.id, isComingSoon]);

  const lessons = stats.lessons;
  const estTime = (lessons && lessons > 0)
    ? `~${Math.max(1, Math.round((lessons * 20) / 60))} hour${Math.round((lessons * 20) / 60) === 1 ? '' : 's'}`
    : '—';

  const badge = (text, color, bg, border) => (
    <span style={{ fontSize: 10, fontWeight: 800, color, background: bg, border: border ? `1px solid ${border}` : 'none', padding: '3px 9px', borderRadius: 99, letterSpacing: 0.6, textTransform: 'uppercase' }}>{text}</span>
  );
  const statRow = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12.5, color: C.dim }}>{label}</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>{value}</span>
    </div>
  );

  const startCta = () => { onSelectTrack(track); onClose(); };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 540, maxHeight: '88vh', overflowY: 'auto', background: C.cream, border: `1px solid ${C.border}`, borderRadius: 18, padding: '24px 26px', position: 'relative', boxShadow: 'var(--shadow-card)' }}>
        <button onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 14, right: 14, width: 30, height: 30, borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.dim, fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>

        {/* Header */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10, paddingRight: 32 }}>
          {badge(track.badge, track.color, `${track.color}18`)}
          {diffStyle && badge(track.difficulty, diffStyle.color, diffStyle.bg, diffStyle.border)}
          {track.categories?.[0] && badge(track.categories[0], 'var(--text-secondary)', 'var(--bg-elevated)')}
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 14px' }}>{track.label}</h2>

        {/* What you'll learn */}
        {track.description && (
          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 6px' }}>What you'll learn</p>
            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>{track.description}</p>
          </div>
        )}

        {/* Exam details — only for tracks with exam logistics */}
        {track.exam_codes && (
          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 6px' }}>Exam details</p>
            {statRow('Exam code(s)', track.exam_codes.join(', '))}
            {track.passing_score != null && statRow('Passing score', String(track.passing_score))}
            {track.exam_fee && statRow('Exam fee (approx.)', track.exam_fee)}
            {track.exam_url && (
              <a href={track.exam_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 12.5, fontWeight: 700, color: AMBER, textDecoration: 'none' }}>
                Official Exam Page →
              </a>
            )}
          </div>
        )}

        {/* Job roles */}
        {track.job_roles?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <p style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' }}>Roles this certification supports</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {track.job_roles.map(role => (
                <span key={role} style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-elevated)', border: `1px solid ${C.border}`, padding: '5px 11px', borderRadius: 99 }}>{role}</span>
              ))}
            </div>
          </div>
        )}

        {/* Track stats */}
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 10, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 6px' }}>Track stats</p>
          {statRow('Lessons', stats.loading ? '…' : (isComingSoon ? 'Curriculum in progress' : (lessons || '—')))}
          {statRow('Estimated study time', stats.loading ? '…' : (isComingSoon ? '—' : estTime))}
          {statRow('Status', statusLabel)}
        </div>

        {/* CTA */}
        {isComingSoon ? (
          <button disabled style={{ width: '100%', padding: '12px', borderRadius: 10, background: C.card, border: `1px solid ${C.border}`, color: C.muted, fontSize: 14, fontWeight: 700, cursor: 'default', fontFamily: 'inherit' }}>
            Notify me when available
          </button>
        ) : (
          <button onClick={startCta} style={{ width: '100%', padding: '13px', borderRadius: 10, background: AMBER, border: 'none', color: '#ffffff', fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.2, boxShadow: '0 4px 14px rgba(217,119,6,0.26)' }}>
            {stats.started ? 'Continue Track' : 'Start Track'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// TRACK GRID — card click opens the detail modal (no longer navigates directly)
// ─────────────────────────────────────────────
function TrackGrid({ trackList, activeTrackId, onOpenDetail }) {
  const [category,   setCategory]   = useState(null);
  const [difficulty, setDifficulty] = useState(null);

  const filtered = trackList.filter(t => {
    const matchesCategory   = !category   || (Array.isArray(t.categories) && t.categories.includes(category));
    const matchesDifficulty = !difficulty || t.difficulty === difficulty;
    return matchesCategory && matchesDifficulty;
  });

  const chipRowStyle = { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '0 0 10px' };

  return (
    <div>
      <div style={chipRowStyle}>
        {[null, ...TRACK_CATEGORIES].map(cat => (
          <FilterChip key={cat || 'all-cat'} label={cat || 'All'} active={category === cat} onClick={() => setCategory(cat)} />
        ))}
      </div>
      <div style={{ ...chipRowStyle, marginBottom: 14 }}>
        {[null, ...TRACK_DIFFICULTIES].map(level => (
          <FilterChip key={level || 'all-level'} label={level || 'All Levels'} active={difficulty === level} onClick={() => setDifficulty(level)} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-secondary)', borderRadius: 12, border: '1px solid var(--border-soft)' }}>
          No tracks match this filter yet.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {filtered.map(track => {
          const isActive        = track.id === activeTrackId;
          const isComingSoon    = COMING_SOON_TRACKS.includes(track.id);
          const isBetaTesting   = BETA_TESTING_TRACKS.has(track.id);
          const showStatusBadge = isComingSoon || isBetaTesting;
          const statusLabel     = isComingSoon ? 'Coming Soon' : 'Beta Testing';
          const statusColor     = isBetaTesting ? AMBER : SLATE;
          const statusBg        = isBetaTesting ? '#fffaf0' : '#f1f3f7';
          const diffStyle       = DIFFICULTY_STYLE[track.difficulty];
          return (
            <button key={track.id} onClick={() => onOpenDetail(track)}
              style={{ position: 'relative', background: isActive ? '#fffaf0' : '#ffffff', border: isActive ? `2px solid ${AMBER}` : '1px solid #e5e7eb', borderRadius: 14, padding: '16px 18px', textAlign: 'left', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit', boxShadow: 'var(--card-shadow)' }}
              onMouseEnter={e => { e.currentTarget.style.background = isActive ? '#fffaf0' : '#f8fafc'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isActive ? '#fffaf0' : '#ffffff'; }}>
              {showStatusBadge && (
                <span style={{ position: 'absolute', top: 10, right: 10, fontSize: 9, fontWeight: 800, color: statusColor, background: statusBg, padding: '3px 8px', borderRadius: 99, letterSpacing: 0.7, textTransform: 'uppercase' }}>{statusLabel}</span>
              )}
              <div style={{ marginBottom: 8, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 9, fontWeight: 800, color: track.color, background: `${track.color}18`, padding: '3px 8px', borderRadius: 99, letterSpacing: 0.7, textTransform: 'uppercase' }}>{track.badge}</span>
                {diffStyle && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: diffStyle.color, background: diffStyle.bg, border: `1px solid ${diffStyle.border}`, padding: '2px 8px', borderRadius: 99, letterSpacing: 0.7, textTransform: 'uppercase' }}>{track.difficulty}</span>
                )}
              </div>
              <p style={{ fontSize: 16, fontWeight: 700, color: INK, margin: '0 0 3px' }}>{track.label}</p>
              <p style={{ fontSize: 12, color: SLATE, margin: 0, fontWeight: 500 }}>{track.exams?.join(' · ')}</p>
              <p style={{ fontSize: 12, color: AMBER, margin: '10px 0 0', fontWeight: 600 }}>View details →</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// DASHBOARD CONTENT — module-level so React keeps component identity across
// parent re-renders.
// ─────────────────────────────────────────────
export function DashboardContent({ learner, onSelectTrack, activeTrackId, trackList, onNavigate }) {
  const [detailTrack, setDetailTrack] = useState(null);
  const gridRef = useRef(null);
  return (
    <div style={{ padding: '28px 32px', overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: INK, margin: '0 0 5px', letterSpacing: -0.3 }}>Browse Certifications</h1>
      <p style={{ fontSize: 14, color: SLATE, margin: '0 0 20px' }}>Choose a track, see what it covers, and start.</p>
      <RoadmapReminderCard
        learner={learner}
        onNavigate={onNavigate}
        onExplore={() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
      />
      <p style={{ fontSize: 11, fontWeight: 700, color: SLATE, textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 10px' }}>
        {activeTrackId ? 'Switch Track' : 'Choose a Track'}
      </p>
      <div ref={gridRef}>
        <TrackGrid trackList={trackList} activeTrackId={activeTrackId} onOpenDetail={setDetailTrack}/>
      </div>
      {detailTrack && (
        <TrackDetailModal track={detailTrack} learner={learner} onClose={() => setDetailTrack(null)} onSelectTrack={onSelectTrack}/>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT — learning-only, full-width (no jobs/career panel)
// ─────────────────────────────────────────────
export default function TrackSelector({ learner, onSelectTrack, onSignOut, onBackToDashboard, theme, toggleTheme }) {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const trackList     = Object.values(TRACKS);
  const activeTrackId = learner?.current_track;

  const headerPad   = isMobile ? '10px 16px' : '10px 24px';
  const logoHeight  = isMobile ? 84 : 108;
  const titleSize   = isMobile ? 16 : 22;

  return (
    <div style={{ height: '100%', background: C.bg, display: 'flex', flexDirection: 'column', fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ padding: headerPad, background: C.panel, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <img src="/logo.jpg" alt="AdaptOS" style={{ height: logoHeight, objectFit: 'contain' }}/>
          <div style={{ width: 1, height: 44, background: C.border, flexShrink: 0 }}/>
          <span style={{ fontSize: titleSize, fontWeight: 800, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>My Dashboard</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onBackToDashboard && (
            <button onClick={onBackToDashboard} style={{ padding: '5px 12px', borderRadius: 7, background: 'var(--bg-elevated)', border: `1px solid ${C.border}`, color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>← Dashboard</button>
          )}
          <button onClick={onSignOut} style={{ padding: '5px 12px', borderRadius: 7, background: 'var(--bg-elevated)', border: `1px solid ${C.border}`, color: C.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Sign Out</button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        <DashboardContent learner={learner} onSelectTrack={onSelectTrack} activeTrackId={activeTrackId} trackList={trackList}/>
      </div>
    </div>
  );
}
